import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { usePlannerActivity, formatRelativeTime } from '../../hooks/usePlannerActivity';
import {
  PlannerPage,
  PlannerCard,
  PlannerKicker,
  PlannerHeading,
  PlannerSubtle,
  PlannerError,
  plannerColors,
} from './plannerTheme';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FaCalendarCheck as FaCalendarCheckRaw,
  FaUserCog as FaUserCogRaw,
  FaTasks as FaTasksRaw,
  FaRegDotCircle as FaRegDotCircleRaw,
} from 'react-icons/fa';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCalendarCheck: any = FaCalendarCheckRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUserCog: any = FaUserCogRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaTasks: any = FaTasksRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaRegDotCircle: any = FaRegDotCircleRaw;

const Timeline = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const TimelineItem = styled.li`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  border-radius: 12px;
  padding: 0.75rem 0.85rem;
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const SummaryText = styled.span`
  color: ${plannerColors.text};
  font-size: 0.84rem;
  font-weight: 600;
`;

const MetaText = styled.span`
  margin-top: 0.3rem;
  display: inline-block;
  color: ${plannerColors.muted};
  font-size: 0.74rem;
`;

const TypeRow = styled.div`
  margin-bottom: 0.38rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

const TypeIconWrap = styled.span<{ $tone: 'info' | 'success' | 'warning' }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'success'
        ? 'rgba(52, 199, 89, 0.4)'
        : $tone === 'warning'
          ? 'rgba(255, 204, 0, 0.4)'
          : 'rgba(141, 176, 255, 0.42)'};
  color: ${({ $tone }) =>
    $tone === 'success'
      ? plannerColors.success
      : $tone === 'warning'
        ? plannerColors.warning
        : '#8db0ff'};
  background: ${({ $tone }) =>
    $tone === 'success'
      ? 'rgba(52, 199, 89, 0.12)'
      : $tone === 'warning'
        ? 'rgba(255, 204, 0, 0.12)'
        : 'rgba(141, 176, 255, 0.14)'};
`;

const TypeLabel = styled.span<{ $tone: 'info' | 'success' | 'warning' }>`
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $tone }) =>
    $tone === 'success'
      ? plannerColors.success
      : $tone === 'warning'
        ? plannerColors.warning
        : '#8db0ff'};
`;

function activityIcon(icon: 'availability' | 'role' | 'service' | 'assignment' | 'generic') {
  if (icon === 'availability') return <FaCalendarCheck size={11} />;
  if (icon === 'role') return <FaUserCog size={11} />;
  if (icon === 'service' || icon === 'assignment') return <FaTasks size={11} />;
  return <FaRegDotCircle size={10} />;
}

const ServiceLink = styled(Link)`
  color: ${plannerColors.accent};
  font-size: 0.75rem;
  font-weight: 700;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const FilterRow = styled.div`
  margin: 0.7rem 0 0.95rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

const FilterChip = styled.button<{ $active?: boolean }>`
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? plannerColors.accent : plannerColors.border)};
  background: ${({ $active }) => ($active ? plannerColors.accentSoft : plannerColors.surfaceHover)};
  color: ${({ $active }) => ($active ? plannerColors.text : plannerColors.muted)};
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.28rem 0.62rem;
  cursor: pointer;

  &:hover {
    border-color: ${plannerColors.accent};
    color: ${plannerColors.text};
  }
`;

type ActivityFilter = 'all' | 'availability' | 'role' | 'assignment';

export default function ChurchPlannerActivityPage() {
  const activity = usePlannerActivity(true, 50);
  const [activeFilter, setActiveFilter] = useState<ActivityFilter>('all');

  const counts = useMemo(() => {
    const all = activity.items.length;
    const availability = activity.items.filter((item) => item.activityIcon === 'availability').length;
    const role = activity.items.filter((item) => item.activityIcon === 'role').length;
    const assignment = activity.items.filter((item) => item.activityIcon === 'assignment').length;
    return { all, availability, role, assignment };
  }, [activity.items]);

  const filteredItems = useMemo(() => {
    if (activeFilter === 'all') return activity.items;
    if (activeFilter === 'availability') {
      return activity.items.filter((item) => item.activityIcon === 'availability');
    }
    if (activeFilter === 'role') {
      return activity.items.filter((item) => item.activityIcon === 'role');
    }
    return activity.items.filter((item) => item.activityIcon === 'assignment');
  }, [activeFilter, activity.items]);

  if (activity.isLoading) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerSubtle>Loading activity…</PlannerSubtle>
        </PlannerCard>
      </PlannerPage>
    );
  }

  if (activity.isError) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerError>Unable to load planner activity right now.</PlannerError>
        </PlannerCard>
      </PlannerPage>
    );
  }

  return (
    <PlannerPage>
      <PlannerCard>
        <PlannerKicker>Timeline</PlannerKicker>
        <PlannerHeading>Activity</PlannerHeading>
        <FilterRow>
          <FilterChip type="button" $active={activeFilter === 'all'} onClick={() => setActiveFilter('all')}>
            All ({counts.all})
          </FilterChip>
          <FilterChip
            type="button"
            $active={activeFilter === 'availability'}
            onClick={() => setActiveFilter('availability')}
          >
            Availability ({counts.availability})
          </FilterChip>
          <FilterChip type="button" $active={activeFilter === 'role'} onClick={() => setActiveFilter('role')}>
            Role Changes ({counts.role})
          </FilterChip>
          <FilterChip
            type="button"
            $active={activeFilter === 'assignment'}
            onClick={() => setActiveFilter('assignment')}
          >
            Assignments ({counts.assignment})
          </FilterChip>
        </FilterRow>
        {filteredItems.length > 0 ? (
          <Timeline>
            {filteredItems.map((item) => (
              <TimelineItem key={item.id}>
                <TypeRow>
                  <TypeIconWrap $tone={item.activityTone}>{activityIcon(item.activityIcon)}</TypeIconWrap>
                  <TypeLabel $tone={item.activityTone}>{item.activityLabel}</TypeLabel>
                </TypeRow>
                <SummaryRow>
                  <SummaryText>{item.summary}</SummaryText>
                  {item.servicePlanId ? (
                    <ServiceLink to={`/church-planner/services/${item.servicePlanId}`}>
                      Open service
                    </ServiceLink>
                  ) : null}
                </SummaryRow>
                <MetaText>
                  {item.actorName} • {formatRelativeTime(item.createdAt)}
                </MetaText>
              </TimelineItem>
            ))}
          </Timeline>
        ) : (
          <PlannerSubtle>
            No activity matched this filter.
          </PlannerSubtle>
        )}
      </PlannerCard>
    </PlannerPage>
  );
}
